import { Alert, Modal } from 'antd'
import { find, isEmpty } from 'lodash'
import { extendObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { Button, Form } from 'react-bootstrap'
import {
  Checkbox,
  DateMonthInput,
  Input
} from '../../components/formComponents'
import LoadingPageOverlay from '../../components/LoadingPageOverlay'
import ThankYouOverlay from '../../components/ThankYouOverlay'
import withRouter from '../../components/withRouter'
import t from '../../service/translate.service'
import {
  buildValidators,
  isValidBIC,
  isValidIBAN,
  maxLength,
  required
} from '../../service/validate'
import ContractVerificationPage from './ContractVerificationPage'
import { StackedNotifications } from '../../components/StackedNotifications'
import { appFeatureEnabled } from '../../service/appFeatures.service'

class MyPaymentDetails extends Component {
  static propTypes = {
    contractStore: PropTypes.object.isRequired,
    location: PropTypes.object,
  }

  constructor(props) {
    super(props)
    this.inputRefs = [
      React.createRef(),
      React.createRef(),
      React.createRef(),
      React.createRef(),
      React.createRef(),
      React.createRef()
    ]
    extendObservable(this, {
      contractStore: props.contractStore,
      submitted: false,
      errorsList: [],
      paymentMethod: '',
      originalPaymentMethod: '',
      isChangingToSepa: false,
      isChangingToSelf: false,
      emailVerificationEnabled: false,
      verificationStarted: false,
      submitSuccess: false,
      validateFailed: false,
      createOrUpdateFailed: false,
      showConfirmDataModal: false,
      showSepaTemporaryDisabledModal: false,
      alreadyValidatedOnce: false,
      canRequestPaymentMethodChange: true,
      futurePaymentMethod: '',
      notifications: [
        {
          content: '',
          contentDe: '',
          seen: false,
          type: ''
        }
      ],
      errorMessage: '',
      earliestStartFromDate: moment
        .utc()
        .startOf('day')
        .add(30, 'days')
        .startOf('month')
        .add(1, 'months')
        .format(), // newly added
      originalSepaDebitValues: {
        sepaAccountHolderFirstName: '',
        sepaAccountHolderLastName: '',
        sepaIban: '',
        sepaBic: '',
        sepaStartFrom: '',
        sepaGrantMandate: false
      },
      // changeToSepaDirectData: {
      //   sepaAccountHolderFirstName: '',
      //   sepaAccountHolderLastName: '',
      //   sepaIban: '',
      //   sepaBic: '',
      //   sepaStartFrom: '',
      //   sepaGrantMandate: false
      // },
      originalSelfPaymentValues: {
        selfPaymentRecipient: '',
        selfPaymentIban: '',
        selfPaymentBic: '',
        selfPaymentStartFrom: '',
        selfPaymentActive: false
      },
      selfPaymentValues: {
        selfPaymentRecipient: '',
        selfPaymentIban: '',
        selfPaymentBic: '',
        selfPaymentStartFrom: '',
        selfPaymentActive: false
      },
      sepaDebitValues: {
        sepaAccountHolderFirstName: '',
        sepaAccountHolderLastName: '',
        sepaIban: '',
        sepaBic: '',
        sepaStartFrom: '',
        sepaGrantMandate: false
      },
      sepaValidatorInputs: {
        sepaAccountHolderFirstName: [required, maxLength(60)],
        sepaAccountHolderLastName: [required, maxLength(60)],
        sepaIban: [required, isValidIBAN],
        sepaBic: [required, isValidBIC],
        sepaStartFrom: [required],
        sepaGrantMandate: [required]
      },
      selfValidatorInputs: {
        selfPaymentStartFrom: [required],
        selfPaymentActive: [required]
      }
    })
  }

  generateErrorList = () => {
    const errors = []
    const validators = this.isChangingToSepa
      ? buildValidators(this.sepaValidatorInputs, true)
      : this.isChangingToSelf
        ? buildValidators(this.selfValidatorInputs, true)
        : {}
    for (let i = 0; i < this.inputRefs.length; i++) {
      const inputRef = this?.inputRefs[i]?.current
      if (
        (i === 4 && this.isChangingToSepa) ||
        (i === 3 && this.isChangingToSelf)
      ) {
        const val = inputRef?.props?.selected
        const key = inputRef?.props?.name
        if (
          validators[key] &&
          validators[key](val, t.RENT_PAYMENT_METHOD_CHANGE_START_DATE)
        ) {
          let err = validators[key](
            val,
            t.RENT_PAYMENT_METHOD_CHANGE_START_DATE
          )
          errors.push(
            <button
              key={i}
              type="button"
              onClick={() => {
                inputRef.setFocus()
              }}
            >
              {err?.replace(/[€\*]/g, '')}
            </button>
          )
        }
      } else if (
        (i === 5 && this.isChangingToSepa) ||
        (i === 4 && this.isChangingToSelf)
      ) {
        const key = i === 5 ? 'sepaGrantMandate' : 'selfPaymentActive'
        const val =
          i === 5
            ? this.sepaDebitValues.sepaGrantMandate
            : this.selfPaymentValues.selfPaymentActive
        const label =
          i === 5
            ? t.RENT_PAYMENT_METHOD_CHANGE_GRANT_SEPA_MANDATE
            : t.RENT_PAYMENT_METHOD_CHANGE_SELF_PAYMENT_ACTIVE
        if (validators[key] && validators[key](val, label)) {
          let err = validators[key](val, label)
          errors.push(
            <button
              key={i}
              type="button"
              onClick={() => {
                inputRef.focus()
              }}
            >
              {err?.replace(/[€\*]/g, '')}
            </button>
          )
        }
      } else {
        const val = inputRef?.value
        const label = inputRef?.ariaLabel
        const key = inputRef?.name
        if (validators[key] && validators[key](val, label)) {
          let err = validators[key](val, label)
          errors.push(
            <button
              key={i}
              type="button"
              onClick={() => {
                inputRef.focus()
              }}
            >
              {err?.replace(/[€\*]/g, '')}
            </button>
          )
        }
      }
    }
    return errors
  }

  AlertContent = () => {
    const errorsList = this.errorsList
    const errorListVoice = this.contractStore.errorListVoice
    if (errorsList?.length > 0) {
      return (
        <Alert
          role={errorListVoice ? 'alert' : 'none'}
          message={
            <div
              className="error-list"
              role={errorListVoice ? 'alert' : 'none'}
              aria-live={errorListVoice ? 'assertive' : 'off'}
            >
              {t.REQUIRED_FIELD_MISSING}
              {errorsList?.map((err) => {
                return err
              })}
            </div>
          }
          type="error"
          style={{ marginTop: '12px', marginBottom: '12px' }}
        />
      )
    } else {
      return <></>
    }
  }

  sepaValidators() {
    return buildValidators(
      this.sepaValidatorInputs,
      this.props.contractStore.submitted
    )
  }

  selfValidators() {
    return buildValidators(
      this.selfValidatorInputs,
      this.props.contractStore.submitted
    )
  }

  componentDidMount = () => {
    window.scrollTo(0, 0)
    this.props.contractStore.submitted = false
    const { location } = this.props
    ;(async () => {
      const details = await this.props.contractStore.getPaymentMethodDetails(
        this.props.useParams.contractNumber
      )
      if (!isEmpty(details)) {
        this.paymentMethod = details.paymentMethod
        this.canRequestPaymentMethodChange =
          details.canRequestPaymentMethodChange
        this.earliestStartFromDate = new Date(details.earliestStartFromDate)
        this.futurePaymentMethod = details.futurePaymentMethod

        if (!this.canRequestPaymentMethodChange) {
          this.notifications = details.notifications.map((notification) => ({
            content: notification.content.replace(
              /selfPayment|sepaDirect/g,
              (match) =>
                match === 'selfPayment' ? t.SELF_PAYMENT : t.DIRECT_DEBIT
            ),
            contentDe: notification.contentDe.replace(
              /selfPayment|sepaDirect/g,
              (match) =>
                match === 'selfPayment' ? t.SELF_PAYMENT : t.DIRECT_DEBIT
            ),
            seen: notification.seen,
            type: notification.type
          }))
        }

        this.originalSepaDebitValues = {
          sepaAccountHolderFirstName: details.sepaAccountHolderFirstName,
          sepaAccountHolderLastName: details.sepaAccountHolderLastName,
          sepaIban: details.sepaIban,
          sepaBic: details.sepaBic,
          sepaStartFrom:
            details.sepaStartFrom !== null
              ? new Date(details.sepaStartFrom)
              : '',
          sepaGrantMandate: details.sepaGrantMandate
        }

        this.originalSelfPaymentValues = {
          selfPaymentRecipient: details.selfPaymentRecipient,
          selfPaymentIban: details.selfPaymentIban,
          selfPaymentBic: details.selfPaymentBic,
          selfPaymentStartFrom:
            details.selfPaymentStartFrom !== null
              ? new Date(details.selfPaymentStartFrom)
              : '',
          selfPaymentActive: details.selfPaymentActive
        }

        // this.changeToSepaDirectData = {
        //   sepaAccountHolderFirstName:
        //     details.changeToSepaDirectData.sepaAccountHolderFirstName,
        //   sepaAccountHolderLastName:
        //     details.changeToSepaDirectData.sepaAccountHolderLastName,
        //   sepaIban: details.changeToSepaDirectData.sepaIban,
        //   sepaBic: details.changeToSepaDirectData.sepaBic,
        //   sepaStartFrom:
        //     details.changeToSepaDirectData.sepaStartFrom !== null
        //       ? new Date(details.changeToSepaDirectData.sepaStartFrom)
        //       : '',
        //   sepaGrantMandate: details.changeToSepaDirectData.sepaGrantMandate
        // }

        this.selfPaymentValues.selfPaymentRecipient =
          details.selfPaymentRecipient
        this.selfPaymentValues.selfPaymentIban = details.selfPaymentIban
        this.selfPaymentValues.selfPaymentBic = details.selfPaymentBic
        this.selfPaymentValues.selfPaymentStartFrom =
          details.selfPaymentStartFrom !== null
            ? new Date(details.selfPaymentStartFrom)
            : ''
        this.selfPaymentValues.selfPaymentActive = details.selfPaymentActive

        if (this.paymentMethod !== 'sepaDirect') {
          this.sepaDebitValues.sepaAccountHolderFirstName =
            details.sepaAccountHolderFirstName
          this.sepaDebitValues.sepaAccountHolderLastName =
            details.sepaAccountHolderLastName
          this.sepaDebitValues.sepaIban = details.sepaIban
          this.sepaDebitValues.sepaBic = details.sepaBic
          this.sepaDebitValues.sepaStartFrom =
            details.sepaStartFrom !== null
              ? new Date(details.sepaStartFrom)
              : ''
          this.sepaDebitValues.sepaGrantMandate = details.sepaGrantMandate
        }
      }

      if (
        !isEmpty(location?.state?.rentPaymentMethodData) &&
        location?.state?.rentPaymentMethodData.changingToMethod
      ) {
        if (
          location?.state?.rentPaymentMethodData.changingToMethod ===
          'sepaDirect'
        ) {
          const response =
            await this.props.contractStore.changePaymentMethodToSepa(
              this.props.useParams.contractNumber,
              {
                sepaAccountHolderFirstName:
                  location?.state?.rentPaymentMethodData
                    .sepaAccountHolderFirstName,
                sepaAccountHolderLastName:
                  location?.state?.rentPaymentMethodData
                    .sepaAccountHolderLastName,
                sepaIban: location?.state?.rentPaymentMethodData.sepaIban,
                sepaBic: location?.state?.rentPaymentMethodData.sepaBic,
                sepaGrantMandate:
                  location?.state?.rentPaymentMethodData.sepaGrantMandate,
                sepaStartFrom:
                  location?.state?.rentPaymentMethodData.sepaStartFrom
              }
            )

          if (response.data.success) {
            this.onCancel()
            this.submitSuccess = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
          } else {
            this.createOrUpdateFailed = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
            this.errorMessage = t[response.data.errors]
          }
        } else if (
          location?.state?.rentPaymentMethodData.changingToMethod ===
          'selfPayment'
        ) {
          const response =
            await this.props.contractStore.changePaymentMethodToSelfPayment(
              this.props.useParams.contractNumber,
              {
                selfPaymentStartFrom:
                  location?.state?.rentPaymentMethodData.selfPaymentStartFrom,
                selfPaymentActive:
                  location?.state?.rentPaymentMethodData.selfPaymentActive
              }
            )

          if (response.data.success) {
            this.onCancel()
            this.submitSuccess = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
          } else {
            this.createOrUpdateFailed = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
            this.errorMessage = t[response.data.errors]
          }
        }
      }
    })()
  }

  translateFormKeys = (key) => {
    switch (key.trim()) {
      case 'sepaAccountHolderFirstName':
        return t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER_FIRST_NAME
      case 'sepaAccountHolderLastName':
        return t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER_LAST_NAME
      case 'sepaIban':
        return t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN
      case 'sepaBic':
        return t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC
      case 'sepaStartFrom':
        return t.RENT_PAYMENT_METHOD_CHANGE_START_DATE
      case 'sepaGrantMandate':
        return t.RENT_PAYMENT_METHOD_CHANGE_GRANT_SEPA_MANDATE
      case 'selfPaymentStartFrom':
        return t.RENT_PAYMENT_METHOD_CHANGE_START_DATE
      case 'selfPaymentActive':
        return t.RENT_PAYMENT_METHOD_CHANGE_SELF_PAYMENT_ACTIVE
      default:
        return key
    }
  }

  handleTempPaymentMethodChange = (paymentMethod) => {
    this.originalPaymentMethod = this.paymentMethod
    this.paymentMethod = paymentMethod
    this.isChangingToSepa = paymentMethod === 'sepaDirect'
    this.isChangingToSelf = paymentMethod === 'selfPayment'
  }

  handleSepaDebitChange = (e) => {
    this.props.contractStore.errorListVoice = false
    this.sepaDebitValues[e.target.name] = e.target.value
  }

  handleSelfPaymentChange = (e) => {
    this.props.contractStore.errorListVoice = false
    this.selfPaymentValues[e.target.name] = e.target.value
  }

  handleValidationBeforeSubmit = () => {
    let isValid = true
    this.props.contractStore.submitted = true
    if (this.isChangingToSepa) {
      const validator = this.sepaValidators()
      isValid = !find(validator, (validate, field) => {
        return validate(this.sepaDebitValues[field])
      })
    }

    if (this.isChangingToSelf) {
      const validator = this.selfValidators()
      isValid = !find(validator, (validate, field) => {
        return validate(this.selfPaymentValues[field])
      })
    }

    return isValid
  }

  handleSubmit = async (e) => {
    e.preventDefault()
    try {
      this.errorsList = []
      this.errorsList = this.generateErrorList()
      this.props.contractStore.errorListVoice = true
      this.submitted = false
      setTimeout(() => {
        if (this.errorsList.length > 0) {
          window.scrollTo(0, 0)
        }
        this.submitted = true
      }, 100)
      const isValid = this.handleValidationBeforeSubmit()
      if (!isValid) {
        return
      }

      if (this.alreadyValidatedOnce) {
        const emailVerificationEnabled =
          appFeatureEnabled("verificationEnabled")
        if (emailVerificationEnabled) {
          const { contractNumber } = this.props.useParams
          const res =
            await this.props.contractStore.getContractVerificationInfo(
              contractNumber
            )
          if (!res) this.emailVerificationEnabled = true
          const status =
            await this.props.contractStore.checkUserVerificationInfo()
          if (!status.email) {
            this.emailVerificationEnabled = true
          }

          if (this.emailVerificationEnabled) {
            this.verificationStarted = true
            return
          }
        }

        if (this.isChangingToSepa) {
          const response =
            await this.props.contractStore.changePaymentMethodToSepa(
              this.props.useParams.contractNumber,
              {
                sepaAccountHolderFirstName:
                  this.sepaDebitValues.sepaAccountHolderFirstName,
                sepaAccountHolderLastName:
                  this.sepaDebitValues.sepaAccountHolderLastName,
                sepaIban: this.sepaDebitValues.sepaIban,
                sepaBic: this.sepaDebitValues.sepaBic,
                sepaStartFrom: moment
                  .utc(this.sepaDebitValues.sepaStartFrom)
                  .add(1, 'day')
                  .startOf('day')
                  .format(),
                sepaGrantMandate: this.sepaDebitValues.sepaGrantMandate
              }
            )

          if (response.data.success) {
            this.onCancel()
            this.submitSuccess = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
          } else {
            this.createOrUpdateFailed = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
            this.errorMessage = t[response.data.errors]
          }
        }

        if (this.isChangingToSelf) {
          const response =
            await this.props.contractStore.changePaymentMethodToSelfPayment(
              this.props.useParams.contractNumber,
              {
                selfPaymentStartFrom: moment
                  .utc(this.selfPaymentValues.selfPaymentStartFrom)
                  .add(1, 'day')
                  .startOf('day')
                  .format(),
                selfPaymentActive: this.selfPaymentValues.selfPaymentActive
              }
            )

          if (response.data.success) {
            this.onCancel()
            this.submitSuccess = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
          } else {
            this.createOrUpdateFailed = true
            this.alreadyValidatedOnce = false
            this.showConfirmDataModal = false
            this.errorMessage = t[response.data.errors]
          }
        }
        return
      }

      const response = await this.props.contractStore.validatePaymentData(
        this.props.useParams.contractNumber,
        this.isChangingToSepa
          ? {
              sepaAccountHolderFirstName:
                this.sepaDebitValues.sepaAccountHolderFirstName,
              sepaAccountHolderLastName:
                this.sepaDebitValues.sepaAccountHolderLastName,
              sepaIban: this.sepaDebitValues.sepaIban,
              sepaBic: this.sepaDebitValues.sepaBic,
              sepaStartFrom: moment
                .utc(this.sepaDebitValues.sepaStartFrom)
                .add(1, 'day')
                .startOf('day')
                .format(),
              sepaGrantMandate: this.sepaDebitValues.sepaGrantMandate
            }
          : {
              selfPaymentStartFrom: moment
                .utc(this.selfPaymentValues.selfPaymentStartFrom)
                .add(1, 'day')
                .startOf('day')
                .format(),
              selfPaymentActive: this.selfPaymentValues.selfPaymentActive
            },
        this.isChangingToSepa ? 'sepaDirect' : 'selfPayment'
      )

      if (!response.success) {
        this.validateFailed = true
        let invalidFields = response.errors.fields.split(',')
        let invalidFieldsTranslated = invalidFields.map((field) => {
          return this.translateFormKeys(field)
        })
        this.errorMessage =
          t.RENT_PAYMENT_METHOD_FORM_VALIDATION_FAILED +
          ' ' +
          invalidFieldsTranslated.join(', ')
        return
      }
      this.errorMessage = ''
      this.validateFailed = false
      this.createOrUpdateFailed = false
      this.showConfirmDataModal = true
      this.alreadyValidatedOnce = true
    } catch (error) {
      console.error('An error occurred:', error)
    }
  }

  onCancel = () => {
    if (this.isChangingToSelf) {
      this.selfPaymentValues = { ...this.originalSelfPaymentValues }
    }

    if (this.isChangingToSepa) {
      this.sepaDebitValues = {}
    }
    this.paymentMethod = this.originalPaymentMethod
    this.isChangingToSepa = false
    this.isChangingToSelf = false
    this.submitSuccess = false
    this.validateFailed = false
    this.createOrUpdateFailed = false
    this.verificationStarted = false
    this.props.contractStore.submitted = false
    this.errorMessage = ''
    this.errorsList = []
    this.submitted = false
  }

  renderForm() {
    return (
      <Form>
        {!this.canRequestPaymentMethodChange &&
          !isEmpty(this.notifications) && (
            <div className="homepage">
              <StackedNotifications
                notifications={this.notifications.map((alert) => ({
                  ...alert,
                  content:
                    localStorage.getItem('locale') === 'en'
                      ? alert.content
                      : alert.contentDe
                }))}
                onContentClick={() => {}}
                onClose={null}
              />
            </div>
          )}
        {(this.validateFailed || this.createOrUpdateFailed) && (
          <Alert
            message={this.errorMessage}
            type="error"
            aria-live="assertive"
            role="alert"
          />
        )}
        {this.submitted && (this.isChangingToSepa || this.isChangingToSelf) && (
          <this.AlertContent />
        )}
        {this.emailVerificationEnabled && this.verificationStarted && (
          <ContractVerificationPage
            isAddingContract={false}
            redirectUrl={`/my-payment-details/${this.props.useParams.contractNumber}`}
            changingToMethod={
              this.isChangingToSepa ? 'sepaDirect' : 'selfPayment'
            }
            rentPaymentMethodData={
              this.isChangingToSepa
                ? {
                    sepaAccountHolderFirstName:
                      this.sepaDebitValues.sepaAccountHolderFirstName,
                    sepaAccountHolderLastName:
                      this.sepaDebitValues.sepaAccountHolderLastName,
                    sepaIban: this.sepaDebitValues.sepaIban,
                    sepaBic: this.sepaDebitValues.sepaBic,
                    sepaStartFrom: moment
                      .utc(this.sepaDebitValues.sepaStartFrom)
                      .add(1, 'day')
                      .startOf('day')
                      .format(),
                    sepaGrantMandate: this.sepaDebitValues.sepaGrantMandate
                  }
                : {
                    selfPaymentStartFrom: moment
                      .utc(this.selfPaymentValues.selfPaymentStartFrom)
                      .add(1, 'day')
                      .startOf('day')
                      .format(),
                    selfPaymentActive: this.selfPaymentValues.selfPaymentActive
                  }
            }
            onCancel={() => {
              this.verificationStarted = false
            }}
          />
        )}
        <div style={{ marginBottom: '23px' }}>
          <Input
            name="contractNumber"
            label={`${t['USER_TENANT_NUMBER']}`}
            type="text"
            value={
              this.props.contractStore.contractNumber ||
              this.props.useParams.contractNumber
            }
            helper={<span>{t.RENT_PAYMENT_METHOD_CHANGE_CONTRACT_INFO}</span>}
            disabled
          />
        </div>
        {!this.isChangingToSepa && !this.isChangingToSelf && (
          <>
            <Input
              name="selectedPaymentMethod"
              label={`${t['RENT_PAYMENT_METHOD_CHANGE_CURRENT_METHOD']}`}
              type="text"
              value={
                this.paymentMethod === 'selfPayment'
                  ? t.SELF_PAYMENT
                  : t.DIRECT_DEBIT
              }
              disabled
            />
            <div style={{ marginTop: 20 }}>
              <p
                className="rentPaymentChangeDetailsTd"
                style={{ fontWeight: 'bold', paddingBottom: 0 }}
              >
                {this.paymentMethod === 'selfPayment'
                  ? t.RENT_PAYMENT_METHOD_CHANGE_SELF_PAYMENT_INFO
                  : t.RENT_PAYMENT_METHOD_CHANGE_SEPA_INFO}
                :
              </p>
            </div>
            <table style={{ fontSize: 20, width: '50%' }}>
              <tbody>
                {this.paymentMethod === 'selfPayment' && (
                  <tr>
                    <strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_RECIPIENT}:
                      </td>
                    </strong>
                    <td className="rentPaymentChangeDetailsTd">
                      {this.selfPaymentValues.selfPaymentRecipient}
                    </td>
                  </tr>
                )}
                {this.paymentMethod === 'sepaDirect' && (
                  <tr>
                    <strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER}:
                      </td>
                    </strong>
                    <td className="rentPaymentChangeDetailsTd">
                      {this.originalSepaDebitValues.sepaAccountHolderFirstName +
                        ' ' +
                        this.originalSepaDebitValues.sepaAccountHolderLastName}
                    </td>
                  </tr>
                )}
                <tr>
                  <strong>
                    <td className="rentPaymentChangeDetailsTd">
                      {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN}:
                    </td>
                  </strong>
                  <td className="rentPaymentChangeDetailsTd">
                    {this.paymentMethod === 'selfPayment'
                      ? this.selfPaymentValues.selfPaymentIban
                      : this.originalSepaDebitValues.sepaIban}
                  </td>
                </tr>
                <tr>
                  <strong>
                    <td className="rentPaymentChangeDetailsTd">
                      {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC}:
                    </td>
                  </strong>
                  <td className="rentPaymentChangeDetailsTd">
                    {this.paymentMethod === 'selfPayment'
                      ? this.selfPaymentValues.selfPaymentBic
                      : this.originalSepaDebitValues.sepaBic}
                  </td>
                </tr>
                {this.paymentMethod === 'selfPayment' && (
                  <tr>
                    <strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_REFERENCE}:
                      </td>
                    </strong>
                    <td className="rentPaymentChangeDetailsTd">
                      {this.props.contractStore.contractNumber
                        ? this.props.contractStore.contractNumber
                        : this.props.useParams.contractNumber}
                    </td>
                  </tr>
                )}
                {this.paymentMethod === 'selfPayment' &&
                  this.selfPaymentValues.selfPaymentStartFrom && (
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_START_DATE}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {moment(
                          this.selfPaymentValues.selfPaymentStartFrom
                        ).format('DD.MM.YYYY')}
                      </td>
                    </tr>
                  )}
                {this.paymentMethod === 'sepaDirect' &&
                  this.originalSepaDebitValues.sepaStartFrom && (
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_START_DATE}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {moment(
                          this.originalSepaDebitValues.sepaStartFrom
                        ).format('DD.MM.YYYY')}
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
            {this.paymentMethod === 'sepaDirect' &&
              !this.canRequestPaymentMethodChange &&
              this.futurePaymentMethod !== 'sepaDirect' && (
                <>
                  <div style={{ marginTop: 20 }}>
                    <p
                      className="rentPaymentChangeDetailsTd"
                      style={{ fontWeight: 'bold', paddingBottom: 0 }}
                    >
                      {t.RENT_PAYMENT_METHOD_CHANGE_SELF_PAYMENT_INFO}:
                    </p>
                  </div>
                  <table style={{ fontSize: 20, width: '50%' }}>
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_RECIPIENT}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {this.selfPaymentValues.selfPaymentRecipient}
                      </td>
                    </tr>
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {this.selfPaymentValues.selfPaymentIban}
                      </td>
                    </tr>
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {this.selfPaymentValues.selfPaymentBic}
                      </td>
                    </tr>
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_REFERENCE}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {this.props.contractStore.contractNumber
                          ? this.props.contractStore.contractNumber
                          : this.props.useParams.contractNumber}
                      </td>
                    </tr>
                  </table>
                </>
              )}
            {/* {this.paymentMethod === 'selfPayment' &&
              !this.canRequestPaymentMethodChange && (
                <>
                  <div style={{ marginTop: 20 }}>
                    <p
                      className="rentPaymentChangeDetailsTd"
                      style={{ fontWeight: 'bold', paddingBottom: 0 }}
                    >
                      {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_INFO_FUTURE}:
                    </p>
                  </div>
                  <table style={{ fontSize: 20, width: '50%' }}>
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {this.changeToSepaDirectData
                          .sepaAccountHolderFirstName +
                          ' ' +
                          this.changeToSepaDirectData.sepaAccountHolderLastName}
                      </td>
                    </tr>
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {this.changeToSepaDirectData.sepaIban}
                      </td>
                    </tr>
                    <tr>
                      <strong>
                        <td className="rentPaymentChangeDetailsTd">
                          {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC}:
                        </td>
                      </strong>
                      <td className="rentPaymentChangeDetailsTd">
                        {this.changeToSepaDirectData.sepaBic}
                      </td>
                    </tr>
                  </table>
                </>
            )} */}
          </>
        )}
      </Form>
    )
  }

  renderSepaForm() {
    const alertValidators = buildValidators(this.sepaValidatorInputs, true)
    return (
      <Form onSubmit={this.handleSubmit}>
        <Input
          ref={this.inputRefs[0]}
          name="sepaAccountHolderFirstName"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER_FIRST_NAME} *`}
          type="text"
          value={this.sepaDebitValues.sepaAccountHolderFirstName}
          onChange={this.handleSepaDebitChange}
          validate={this.sepaValidators()['sepaAccountHolderFirstName']}
          blurValidate={alertValidators['sepaAccountHolderFirstName']}
          autoComplete="given-name"
        />
        <Input
          ref={this.inputRefs[1]}
          name="sepaAccountHolderLastName"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER_LAST_NAME} *`}
          type="text"
          value={this.sepaDebitValues.sepaAccountHolderLastName}
          onChange={this.handleSepaDebitChange}
          validate={this.sepaValidators()['sepaAccountHolderLastName']}
          blurValidate={alertValidators['sepaAccountHolderLastName']}
          autoComplete="family-name"
        />
        <Input
          ref={this.inputRefs[2]}
          name="sepaIban"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN} *`}
          type="text"
          value={this.sepaDebitValues.sepaIban}
          onChange={this.handleSepaDebitChange}
          validate={this.sepaValidators()['sepaIban']}
          blurValidate={alertValidators['sepaIban']}
        />
        <Input
          ref={this.inputRefs[3]}
          name="sepaBic"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC} *`}
          type="text"
          value={this.sepaDebitValues.sepaBic}
          onChange={this.handleSepaDebitChange}
          validate={this.sepaValidators()['sepaBic']}
          blurValidate={alertValidators['sepaBic']}
        />
        <DateMonthInput
          ref={this.inputRefs[4]}
          name="sepaStartFrom"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_START_DATE} *`}
          value={this.sepaDebitValues.sepaStartFrom}
          onChange={(date) => {
            this.sepaDebitValues.sepaStartFrom = date
          }}
          minDate={this.earliestStartFromDate}
          validate={this.sepaValidators()['sepaStartFrom']}
          blurValidate={alertValidators['sepaStartFrom']}
        />
        <Checkbox
          ref={this.inputRefs[5]}
          name="sepaGrantMandate"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_GRANT_SEPA_MANDATE} *`}
          value={this.sepaDebitValues.sepaGrantMandate}
          onChange={(e) => {
            this.sepaDebitValues.sepaGrantMandate = e.target.checked
          }}
          validate={this.sepaValidators()['sepaGrantMandate']}
          blurValidate={alertValidators['sepaGrantMandate']}
        />
        {this.renderActionButtons()}
      </Form>
    )
  }

  renderSelfPaymentForm() {
    const alertValidators = buildValidators(this.selfValidatorInputs, true)
    return (
      <Form onSubmit={this.handleSubmit}>
        <Input
          ref={this.inputRefs[0]}
          name="selfPaymentRecipient"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER} *`}
          type="text"
          value={this.selfPaymentValues.selfPaymentRecipient}
          onChange={this.handleSelfPaymentChange}
          disabled
        />
        <Input
          ref={this.inputRefs[1]}
          name="selfPaymentIban"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN} *`}
          type="text"
          value={this.selfPaymentValues.selfPaymentIban}
          onChange={this.handleSelfPaymentChange}
          disabled
        />
        <Input
          ref={this.inputRefs[2]}
          name="selfPaymentBic"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC} *`}
          type="text"
          value={this.selfPaymentValues.selfPaymentBic}
          onChange={this.handleSelfPaymentChange}
          disabled
        />
        <DateMonthInput
          ref={this.inputRefs[3]}
          name="selfPaymentStartFrom"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_START_DATE} *`}
          value={this.selfPaymentValues.selfPaymentStartFrom}
          onChange={(date) => {
            this.selfPaymentValues.selfPaymentStartFrom = date
          }}
          minDate={this.earliestStartFromDate}
          validate={this.selfValidators()['selfPaymentStartFrom']}
          blurValidate={alertValidators['selfPaymentStartFrom']}
        />
        <Checkbox
          ref={this.inputRefs[4]}
          name="selfPaymentActive"
          label={`${t.RENT_PAYMENT_METHOD_CHANGE_SELF_PAYMENT_ACTIVE} *`}
          value={this.selfPaymentValues.selfPaymentActive}
          onChange={(e) => {
            this.selfPaymentValues.selfPaymentActive = e.target.checked
          }}
          validate={this.selfValidators()['selfPaymentActive']}
          blurValidate={alertValidators['selfPaymentActive']}
        />
        {this.renderActionButtons()}
      </Form>
    )
  }

  renderActionButtons() {
    return (
      <div
        style={{
          marginTop: 40,
          display: 'flex',
          justifyContent: 'center',
          gap: 10
        }}
      >
        {(this.isChangingToSepa || this.isChangingToSelf) && (
          <button className="button primary" type="submit">
            {t.FORM_SUBMIT}
          </button>
        )}

        <button
          disabled={!this.canRequestPaymentMethodChange}
          className={`button ${
            this.isChangingToSepa || this.isChangingToSelf
              ? 'secondary'
              : 'primary'
          }`}
          type="reset"
          onClick={() => {
            this.isChangingToSepa || this.isChangingToSelf
              ? this.onCancel()
              : this.handleTempPaymentMethodChange(
                  this.paymentMethod === 'selfPayment'
                    ? 'sepaDirect'
                    : 'selfPayment'
                )
          }}
        >
          {this.isChangingToSepa || this.isChangingToSelf
            ? t.FORM_CANCEL
            : this.paymentMethod === 'selfPayment'
              ? t.RENT_PAYMENT_METHOD_CHANGE_DIRECT_DEBIT
              : t.RENT_PAYMENT_METHOD_CHANGE_SELF_PAYMENT_SELF}
        </button>

        {this.paymentMethod === 'sepaDirect' && !this.isChangingToSepa && (
          <button
            disabled={!this.canRequestPaymentMethodChange}
            className="button primary"
            type="submit"
            onClick={() => {
              // this.handleTempPaymentMethodChange('sepaDirect')
              this.showSepaTemporaryDisabledModal = true
            }}
          >
            {t.CHANGE_SEPA_DETAILS}
          </button>
        )}
      </div>
    )
  }

  render() {
    const { role, partnerId } = this.props.contractStore
    const contractNumber =
      this?.props?.contractStore?.selectedContract?.contractNumber

    if (this.props.contractStore.loading) {
      return <LoadingPageOverlay />
    }

    if (
      !appFeatureEnabled("pageChangeRentPaymentMethodVisibleTo",[{ ...this?.props?.contractStore?.selectedContract, role }])
    ) {
      this.props.navigate(`/contract-overview/${contractNumber}`)
      return null
    }

    return (
      <div className="subpageContent">
        {this.submitSuccess ? (
          <ThankYouOverlay
            title={t.THANK_YOU}
            description={t.ADD_RENT_PAYMENT_METHOD}
            buttonText={t.BACK_HOME}
            buttonLink="/"
            handleClick={this.handleOk}
          />
        ) : (
          <>
            {this.showConfirmDataModal && (
              <Modal
                className="modalSubjectWrapper"
                open={this.showConfirmDataModal}
                title={t.RENT_PAYMENT_METHOD_CHANGE_CONFIRM_DATA}
                closable={false}
                centered={true}
                footer={[
                  <button
                    key="back"
                    className="button secondary"
                    onClick={() => {
                      this.showConfirmDataModal = false
                      this.alreadyValidatedOnce = false
                    }}
                    style={{ marginRight: 15 }}
                  >
                    {t.FORM_CANCEL}
                  </button>,
                  <button
                    key="submit"
                    className="button primary"
                    onClick={this.handleSubmit}
                  >
                    {t.FORM_CONFIRM}
                  </button>
                ]}
              >
                {this.isChangingToSelf ? (
                  <div>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER}:{' '}
                      {this.selfPaymentValues.selfPaymentRecipient}
                    </p>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN}:{' '}
                      {this.selfPaymentValues.selfPaymentIban}
                    </p>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC}:{' '}
                      {this.selfPaymentValues.selfPaymentBic}
                    </p>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_START_DATE}:{' '}
                      {moment(
                        this.selfPaymentValues.selfPaymentStartFrom
                      ).format('MM.YYYY')}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_ACCOUNT_HOLDER}:{' '}
                      {this.sepaDebitValues.sepaAccountHolderFirstName +
                        ' ' +
                        this.sepaDebitValues.sepaAccountHolderLastName}
                    </p>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_IBAN}:{' '}
                      {this.sepaDebitValues.sepaIban}
                    </p>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_SEPA_BIC}:{' '}
                      {this.sepaDebitValues.sepaBic}
                    </p>
                    <p>
                      {t.RENT_PAYMENT_METHOD_CHANGE_START_DATE}:{' '}
                      {moment(this.sepaDebitValues.sepaStartFrom).format(
                        'MM.YYYY'
                      )}
                    </p>
                  </div>
                )}
              </Modal>
            )}
            {this.showSepaTemporaryDisabledModal && (
              <Modal
                className="modalSubjectWrapper"
                open={this.showSepaTemporaryDisabledModal}
                title={t.RENT_PAYMENT_METHOD_CHANGE_SEPA_TEMPORARY_DISABLED}
                closable={true}
                centered={true}
                footer={[
                  <button
                    key="back"
                    className="button primary"
                    onClick={() => {
                      this.showSepaTemporaryDisabledModal = false
                    }}
                  >
                    {t.FORM_OK}
                  </button>
                ]}
              />
            )}
            <div
              className="rentPaymentChangeDetailsTd"
              style={{
                fontWeight: 'bold',
                textAlign: 'center',
                ...(this.notifications.length > 0
                  ? { marginBottom: '20px' }
                  : {})
              }}
            >
              {t.RENT_PAYMENT_METHOD_CHANGE}
            </div>
            {this.renderForm()}
            {this.isChangingToSepa
              ? this.renderSepaForm()
              : this.isChangingToSelf
                ? this.renderSelfPaymentForm()
                : null}
            {!this.isChangingToSepa &&
              !this.isChangingToSelf &&
              this.renderActionButtons()}
          </>
        )}
      </div>
    )
  }
}

export default inject(
  'contractStore',
)(withRouter(observer(MyPaymentDetails)))
